import { User } from 'oidc-client-ts';

export enum J2LRole {
  J2LAdmin = 'J2LAdmin',
  Person = 'Person',
  EnterpriseAdmin = 'EnterpriseAdmin',
  EnterpriseUser = 'EnterpriseUser',
  BackofficeUser = 'BackofficeUser'
}

export type J2LRoles = 'J2LAdmin' | 'Person' | 'EnterpriseAdmin' | 'EnterpriseUser' | 'BackofficeUser';

export enum BackofficeUserRole {
  ContractManagement = 'ContractManagement',
  FeatureManagement = 'FeatureManagement',
  RolesManagement = 'RolesManagement',
  ContactAgent = 'ContactAgent',
  ReviewsManagement = 'ReviewsManagement',
  MeetingManagement = 'MeetingManagement',
  PersonProfileManagement = 'PersonProfileManagement',
  EnterpriseProfileManagement = 'EnterpriseProfileManagement',
  ObservatoryManagement = 'ObservatoryManagement',
  BackofficeUsersManagement = 'BackofficeUsersManagement',
  SupportInbox = 'SupportInbox'
}

export type BackofficeUserRoles =
  | 'ContractManagement'
  | 'FeatureManagement'
  | 'RolesManagement'
  | 'ContactAgent'
  | 'ReviewsManagement'
  | 'MeetingManagement'
  | 'PersonProfileManagement'
  | 'EnterpriseProfileManagement'
  | 'ObservatoryManagement'
  | 'BackofficeUsersManagement'
  | 'SupportInbox';

export enum J2LClaim {
  EnterpriseName = 'EnterpriseName'
}

export type J2LClaims = 'EnterpriseName';

export const sortedRoles: J2LRoles[] = ['J2LAdmin', 'Person', 'EnterpriseAdmin', 'EnterpriseUser', 'BackofficeUser'];

export const getRoles = (user: User | null | undefined): J2LRoles[] => {
  return getClaims(user, 'role') || [];
};

export const isInRole = (user: User | null | undefined, role: J2LRoles): boolean => {
  return getRoles(user).includes(role);
};

export const getClaim = <T = string>(user: User | null | undefined, claimName: J2LClaims | string): T => {
  return user?.profile?.[claimName] as T;
};

export const getClaims = <T = string>(user: User | null | undefined, claimName: J2LClaims | string): T[] => {
  const item = user?.profile?.[claimName];
  if (item === null) return [];
  if (Array.isArray(item)) return (item || []) as T[];
  return [item as T];
};

export const getEmail = (user: User | null | undefined): string => {
  return getClaim(user, 'email') || getClaim(user, 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress');
};

export const isEmailVerified = (user: User | null | undefined): boolean => {
  return getClaim<boolean>(user, 'email_verified') === true;
};

export const getFirstName = (user: User | null | undefined): string => {
  return getClaim(user, 'given_name') || getClaim(user, 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname');
};

export const getSurName = (user: User | null | undefined): string => {
  return getClaim(user, 'family_name') || getClaim(user, 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname');
};

export const getName = (user: User | null | undefined): string => {
  return getClaim(user, 'name');
};

export const getPreferredUsername = (user: User | null | undefined): string => {
  return getClaim(user, 'preferred_username');
};

export const getUserId = (user: User | null | undefined): string => {
  return getClaim(user, 'sub');
};

export const getEnterpriseName = (user: User | null | undefined): string => {
  return getClaim(user, 'EnterpriseName');
};

/**
 * Precedencia de roles, de mas a menos mandona.
 *
 * El reparto por aplicacion es el mismo que aplica `Helpers.GetRedirectUrl` en j2l-identity -- back
 * office para `J2LAdmin` y `BackofficeUser`, web para los otros tres -- y ese es justo el punto:
 * mientras el servidor y las dos SPA no repartan igual, alguien con rol de back office y rol de web
 * aterriza en un sitio distinto segun quien haga la cuenta, y las dos apps se lo rebotan sin fin
 * (issue 825). El orden *dentro* de cada grupo no existe en el servidor, que son dos condiciones
 * sueltas; lo fija este fichero para que la respuesta sea siempre la misma.
 *
 * Es un `Record` y no un array para que anadir un rol a `J2LRoles` y olvidarse de esto sea un error
 * de compilacion. Con un array, el olvido se traduce en `resolvePrimaryRole` devolviendo `undefined`
 * y en el usuario aterrizando en ningun sitio, en silencio.
 *
 * No se reutiliza `sortedRoles`: alli `BackofficeUser` va el ultimo, asi que para
 * `["BackofficeUser","Person"]` daria `Person` y contradiria a `resolveAppForUser`.
 */
const rolePriority: Record<J2LRoles, number> = {
  J2LAdmin: 0,
  BackofficeUser: 1,
  Person: 2,
  EnterpriseAdmin: 3,
  EnterpriseUser: 4
};

export const rolePrecedence: J2LRoles[] = (Object.keys(rolePriority) as J2LRoles[]).sort((a, b) => rolePriority[a] - rolePriority[b]);

export type J2LAppTarget = 'website' | 'backoffice' | 'none';

/** El rol que manda para este usuario, o `undefined` si solo tiene el rol base `User`. */
export const resolvePrimaryRole = (user: User | null | undefined): J2LRoles | undefined => {
  const roles = getRoles(user);
  return rolePrecedence.find((role) => roles.includes(role));
};

/**
 * A que aplicacion pertenece un usuario. Fuente unica de verdad para la web y para el back office:
 * si las dos preguntan aqui, no pueden estar en desacuerdo sobre a donde mandarle.
 */
export const resolveAppForUser = (user: User | null | undefined): J2LAppTarget => {
  const role = resolvePrimaryRole(user);
  if (role == null) return 'none';
  return role === J2LRole.J2LAdmin || role === J2LRole.BackofficeUser ? 'backoffice' : 'website';
};
